import React, { useCallback, useEffect, useState, useRef, Children } from 'react';
import { useLocation } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Calendar, MessageCircle, Download, Plus, Minus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
type PriceItem = {
  name: {
    en: string;
    ua: string;
    ru: string;
    ro: string;
  };
  price: string;
};
type Category = {
  id: string;
  anchorId: string;
  title: {
    en: string;
    ua: string;
    ru: string;
    ro: string;
  };
  items: PriceItem[];
};
function AccordionItem({
  category,
  isOpen,
  onToggle,
  lang,
  isHighlighted
}: {
  category: Category;
  isOpen: boolean;
  onToggle: () => void;
  lang: 'en' | 'ua' | 'ru' | 'ro';
  isHighlighted?: boolean;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLButtonElement>(null);
  const [height, setHeight] = useState(0);
  useEffect(() => {
    if (isOpen && contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
      const isMobile = window.innerWidth < 1024;
      if (isMobile && headerRef.current) {
        setTimeout(() => {
          const el = headerRef.current;
          if (el) {
            const top = el.getBoundingClientRect().top + window.scrollY - 100;
            window.scrollTo({
              top,
              behavior: 'smooth'
            });
          }
        }, 150);
      }
    } else {
      setHeight(0);
    }
  }, [isOpen]);
  return <div id={category.anchorId} className={`border-b border-beige/60 last:border-b-0 transition-all duration-500 rounded-lg ${isHighlighted ? 'ring-2 ring-forest-500 bg-ivory-200/50' : ''}`} data-id="element-2848">
      <button ref={headerRef} onClick={onToggle} className="w-full flex items-center justify-between min-h-[56px] py-4 px-2 text-left cursor-pointer select-none" aria-expanded={isOpen} data-id="element-2849">
        <span className={`text-[15px] font-semibold transition-colors duration-200 ${isOpen ? 'text-forest-600' : 'text-charcoal'}`} data-id="element-2850">
          {category.title[lang]}
        </span>
        <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-250 ${isOpen ? 'bg-forest-600 text-white' : 'bg-stone-200 text-stone-500'}`} data-id="element-2851">
          {isOpen ? <Minus className="w-3.5 h-3.5" strokeWidth={2.5} data-id="element-2852" /> : <Plus className="w-3.5 h-3.5" strokeWidth={2.5} data-id="element-2853" />}
        </span>
      </button>

      <div style={{
      maxHeight: isOpen ? `${height}px` : '0px'
    }} className="overflow-hidden transition-[max-height] duration-250 ease-[cubic-bezier(0.4,0,0.2,1)]" data-id="element-2854">
        <div ref={contentRef} className={`pb-6 px-2 transition-opacity duration-200 ${isOpen ? 'opacity-100' : 'opacity-0'}`} data-id="element-2855">
          <div className="space-y-3.5" data-id="element-2856">
            {category.items.map((item, idx) => <div key={idx} className="flex items-baseline gap-2" data-id="element-2857">
                <span className="text-[13px] text-charcoal-light leading-snug" data-id="element-2858">
                  {item.name[lang]}
                </span>
                <span className="flex-grow border-b border-dotted border-beige min-w-[16px] relative top-[-3px]" data-id="element-2859" />
                <span className="text-[13px] font-bold text-forest-700 whitespace-nowrap tabular-nums" data-id="element-2860">
                  {item.price}
                </span>
              </div>)}
          </div>
          <div className="mt-6 flex flex-col sm:flex-row gap-3" data-id="element-2861">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-2862">
              <Button size="sm" className="w-full gap-2" data-id="element-2863">
                <Calendar className="w-4 h-4" data-id="element-2864" />
                {lang === 'en' ? 'Book Online' : lang === 'ua' ? 'Записатись онлайн' : lang === 'ro' ? 'Programare Online' : 'Записаться онлайн'}
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-2865">
              <Button variant="whatsapp" size="sm" className="w-full gap-2" data-id="element-2866">
                <MessageCircle className="w-4 h-4" data-id="element-2867" />
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>;
}
export function PricesPage() {
  const {
    t,
    lang
  } = useTranslation();
  const location = useLocation();
  const [openId, setOpenId] = useState<string | null>('consultations');
  const seoTitle = lang === 'en' ? 'Dental Prices in Angel, London | Dentico Clinic' : lang === 'ua' ? 'Ціни на стоматологію в Angel, Лондон | Dentico Clinic' : lang === 'ro' ? 'Prețuri Dentare în Angel, Londra | Dentico Clinic' : 'Цены на стоматологию в Angel, Лондон | Dentico Clinic';
  const seoDescription = lang === 'en' ? 'View transparent private dental prices at Dentico Clinic in Angel, London. Fees for hygiene, fillings, crowns, implants, orthodontics and more.' : lang === 'ua' ? 'Перегляньте прозорі ціни на приватну стоматологію в Dentico Clinic в Angel, Лондон. Вартість гігієни, пломб, коронок, імплантів, ортодонтії та іншого.' : lang === 'ro' ? 'Vizualizați prețurile transparente pentru stomatologie privată la Dentico Clinic din Angel, Londra. Tarife pentru igienă, plombe, coroane, implanturi, ortodonție și multe altele.' : 'Посмотрите прозрачные цены на частную стоматологию в Dentico Clinic в Angel, Лондон. Стоимость гигиены, пломб, коронок, имплантов, ортодонтии и многого другого.';
  const [highlightedAnchor, setHighlightedAnchor] = useState<string | null>(null);
  const hasScrolledRef = useRef(false);
  const toggle = useCallback((id: string) => {
    setOpenId(prev => prev === id ? null : id);
  }, []);
  const categories: Category[] = [{
    id: 'consultations',
    anchorId: 'consultations',
    title: {
      en: 'Consultations',
      ua: 'Консультації',
      ru: 'Консультации',
      ro: 'Consultații'
    },
    items: [{
      name: {
        en: 'New Patient Exam (incl. 2 X-Rays)',
        ua: 'Огляд нового пацієнта (вкл. 2 знімки)',
        ru: 'Осмотр нового пациента (вкл. 2 снимка)',
        ro: 'Examinare Pacient Nou (incl. 2 Radiografii)'
      },
      price: '£60'
    }, {
      name: {
        en: 'Routine Check Up',
        ua: 'Плановий огляд',
        ru: 'Плановый осмотр',
        ro: 'Control de Rutină'
      },
      price: '£50'
    }, {
      name: {
        en: 'Emergency Appointment',
        ua: 'Екстрений прийом',
        ru: 'Экстренный прием',
        ro: 'Consultație de Urgență'
      },
      price: '£75'
    }, {
      name: {
        en: 'Orthodontics Consultation',
        ua: 'Консультація ортодонта',
        ru: 'Консультация ортодонта',
        ro: 'Consultație Ortodontică'
      },
      price: '£70'
    }]
  }, {
    id: 'hygiene',
    anchorId: 'dental-hygiene',
    title: {
      en: 'Dental Hygiene',
      ua: 'Гігієна',
      ru: 'Гигиена',
      ro: 'Igienă Dentară'
    },
    items: [{
      name: {
        en: 'Scale & Polish',
        ua: 'Чистка та полірування',
        ru: 'Чистка и полировка',
        ro: 'Detartraj și Periaj'
      },
      price: '£95'
    }, {
      name: {
        en: 'Airflow Stain Removal',
        ua: 'Видалення нальоту Airflow',
        ru: 'Удаление налета Airflow',
        ro: 'Îndepărtarea Petelor cu Airflow'
      },
      price: '£45'
    }, {
      name: {
        en: 'Scale & Polish + Airflow + Whitening',
        ua: 'Чистка + Airflow + відбілювання',
        ru: 'Чистка + Airflow + отбеливание',
        ro: 'Detartraj + Airflow + Albire'
      },
      price: '£350'
    }, {
      name: {
        en: 'Deep Cleaning (per quadrant)',
        ua: 'Глибока чистка (1 квадрант)',
        ru: 'Глубокая чистка (1 квадрант)',
        ro: 'Curățare Profundă (per cadran)'
      },
      price: '£190'
    }, {
      name: {
        en: 'Curettage (1–3 teeth)',
        ua: 'Кюретаж (1–3 зуби)',
        ru: 'Кюретаж (1–3 зуба)',
        ro: 'Chiuretaj (1–3 dinți)'
      },
      price: '£60–80'
    }]
  }, {
    id: 'general',
    anchorId: 'fillings-restorations',
    title: {
      en: 'Fillings & Restorations',
      ua: 'Пломби та реставрації',
      ru: 'Пломбы и реставрации',
      ro: 'Plombe și Restaurări'
    },
    items: [{
      name: {
        en: 'Composite Filling (small)',
        ua: 'Композитна пломба (мала)',
        ru: 'Композитная пломба (малая)',
        ro: 'Plombă Compozită (mică)'
      },
      price: '£120–150'
    }, {
      name: {
        en: 'Composite Filling (medium)',
        ua: 'Композитна пломба (середня)',
        ru: 'Композитная пломба (средняя)',
        ro: 'Plombă Compozită (medie)'
      },
      price: '£150–180'
    }, {
      name: {
        en: 'Composite Filling (large)',
        ua: 'Композитна пломба (велика)',
        ru: 'Композитная пломба (большая)',
        ro: 'Plombă Compozită (mare)'
      },
      price: '£180–250'
    }, {
      name: {
        en: 'Composite Bonding',
        ua: 'Композитний бондинг',
        ru: 'Композитный бондинг',
        ro: 'Bonding Compozit'
      },
      price: '£250'
    }, {
      name: {
        en: 'Tooth Restoration / Build up',
        ua: 'Реставрація / нарощування зуба',
        ru: 'Реставрация / наращивание зуба',
        ro: 'Restaurare / Reconstituire Dinte'
      },
      price: '£200'
    }, {
      name: {
        en: 'Fiber Post',
        ua: 'Скловолоконний штифт',
        ru: 'Стекловолоконный штифт',
        ro: 'Pivot din Fibră'
      },
      price: '£150'
    }, {
      name: {
        en: 'Temporary Filling',
        ua: 'Тимчасова пломба',
        ru: 'Временная пломба',
        ro: 'Plombă Provizorie'
      },
      price: '£50'
    }, {
      name: {
        en: 'Re-cement Crown (each)',
        ua: 'Цементування коронки (1 шт.)',
        ru: 'Цементирование коронки (1 шт.)',
        ro: 'Recimentare Coroană'
      },
      price: '£80–119'
    }]
  }, {
    id: 'children',
    anchorId: 'children',
    title: {
      en: "Children's Dental Treatment",
      ua: 'Дитяча стоматологія',
      ru: 'Детская стоматология',
      ro: 'Tratament Dentar Pediatric'
    },
    items: [{
      name: {
        en: 'Routine Dental Examination',
        ua: 'Плановий огляд',
        ru: 'Плановый осмотр',
        ro: 'Examinare Dentară de Rutină'
      },
      price: '£60'
    }, {
      name: {
        en: 'Child Adaptation Visit / Introductory Visit',
        ua: 'Адаптація дитини до візиту / ознайомчий візит',
        ru: 'Адаптация ребёнка к визиту / ознакомительный визит',
        ro: 'Vizită de adaptare a copilului / vizită introductivă'
      },
      price: '£60'
    }, {
      name: {
        en: 'Dental Hygiene / Cleaning (child)',
        ua: 'Гігієна / чистка (дитина)',
        ru: 'Гигиена / чистка (ребенок)',
        ro: 'Igienă / Curățare (copil)'
      },
      price: '£65'
    }, {
      name: {
        en: 'Fluoride Application',
        ua: 'Фторування',
        ru: 'Фторирование',
        ro: 'Aplicare Fluor'
      },
      price: '£25'
    }, {
      name: {
        en: 'Fissure Sealant (per tooth)',
        ua: 'Герметизація фісур (1 зуб)',
        ru: 'Герметизация фиссур (1 зуб)',
        ro: 'Sigilare Fisuri'
      },
      price: '£50'
    }, {
      name: {
        en: 'White Filling (baby tooth)',
        ua: 'Біла пломба (молочний зуб)',
        ru: 'Белая пломба (молочный зуб)',
        ro: 'Plombă Albă (dinte de lapte)'
      },
      price: '£80–120'
    }, {
      name: {
        en: 'Metal Crown (Stainless Steel Crown)',
        ua: 'Металева коронка (дитяча)',
        ru: 'Металлическая коронка (детская)',
        ro: 'Coroană metalică pediatrică'
      },
      price: '£180'
    }, {
      name: {
        en: 'Simple Extraction (baby / primary tooth)',
        ua: 'Просте видалення (молочний зуб)',
        ru: 'Простое удаление (молочный зуб)',
        ro: 'Extracție Simplă (dinte de lapte)'
      },
      price: '£50–120'
    }, {
      name: {
        en: 'Pulpotomy',
        ua: 'Пульпотомія',
        ru: 'Пульпотомия',
        ro: 'Pulpotomie'
      },
      price: '£180–200'
    }, {
      name: {
        en: 'Root Canal Treatment (baby tooth)',
        ua: 'Лікування каналів (молочний зуб)',
        ru: 'Лечение каналов (молочный зуб)',
        ro: 'Tratament de Canal (dinte de lapte)'
      },
      price: '£200–250'
    }]
  }, {
    id: 'root-canal',
    anchorId: 'root-canal',
    title: {
      en: 'Root Canal Treatment',
      ua: 'Лікування каналів',
      ru: 'Лечение каналов',
      ro: 'Tratament de Canal'
    },
    items: [{
      name: {
        en: 'Incisor',
        ua: 'Різець',
        ru: 'Резец',
        ro: 'Incisiv'
      },
      price: '£400'
    }, {
      name: {
        en: 'Premolar',
        ua: 'Премоляр',
        ru: 'Премоляр',
        ro: 'Premolar'
      },
      price: '£500'
    }, {
      name: {
        en: 'Molar',
        ua: 'Моляр',
        ru: 'Моляр',
        ro: 'Molar'
      },
      price: '£600'
    }, {
      name: {
        en: 'Re-Root (Incisor)',
        ua: 'Перелікування (різець)',
        ru: 'Перелечивание (резец)',
        ro: 'Retratament Canal (Incisiv)'
      },
      price: '£600'
    }, {
      name: {
        en: 'Re-Root (Premolar)',
        ua: 'Перелікування (премоляр)',
        ru: 'Перелечивание (премоляр)',
        ro: 'Retratament Canal (Premolar)'
      },
      price: '£750'
    }, {
      name: {
        en: 'Re-Root (Molar)',
        ua: 'Перелікування (моляр)',
        ru: 'Перелечивание (моляр)',
        ro: 'Retratament Canal (Molar)'
      },
      price: '£850'
    }]
  }, {
    id: 'cosmetic',
    anchorId: 'whitening',
    title: {
      en: 'Whitening & Cosmetic',
      ua: 'Відбілювання та естетика',
      ru: 'Отбеливание и эстетика',
      ro: 'Albire și Estetică'
    },
    items: [{
      name: {
        en: 'Clinical Teeth Whitening',
        ua: 'Клінічне відбілювання',
        ru: 'Клиническое отбеливание',
        ro: 'Albire Dentară Clinică'
      },
      price: '£280'
    }, {
      name: {
        en: 'Home Whitening Kit',
        ua: 'Домашній набір для відбілювання',
        ru: 'Домашний набор для отбеливания',
        ro: 'Kit de Albire la Domiciliu'
      },
      price: '£275'
    }, {
      name: {
        en: 'Intracanal Bleaching (1 tooth)',
        ua: 'Внутрішньо-канальне відбілювання (1 зуб)',
        ru: 'Внутриканальное отбеливание (1 зуб)',
        ro: 'Albire Intracanală (1 dinte)'
      },
      price: '£150'
    }]
  }, {
    id: 'extraction',
    anchorId: 'extractions',
    title: {
      en: 'Tooth Extraction & Oral Surgery',
      ua: 'Видалення та хірургія',
      ru: 'Удаление и хирургия',
      ro: 'Extracții și Chirurgie Orală'
    },
    items: [{
      name: {
        en: 'Simple Extraction',
        ua: 'Просте видалення',
        ru: 'Простое удаление',
        ro: 'Extracție Simplă'
      },
      price: 'from £120'
    }, {
      name: {
        en: 'Surgical Extraction',
        ua: 'Хірургічне видалення',
        ru: 'Хирургическое удаление',
        ro: 'Extracție Chirurgicală'
      },
      price: 'from £180–250'
    }, {
      name: {
        en: 'Wisdom Tooth Removal',
        ua: 'Видалення зуба мудрості',
        ru: 'Удаление зуба мудрости',
        ro: 'Extracția Molarului de Minte'
      },
      price: 'from £180–350'
    }, {
      name: {
        en: 'Apicectomy',
        ua: 'Апікоектомія',
        ru: 'Апикоэктомия',
        ro: 'Apicectomie'
      },
      price: '£600'
    }, {
      name: {
        en: 'Bone Grafting',
        ua: 'Кісткова пластика',
        ru: 'Костная пластика',
        ro: 'Grefă Osoasă'
      },
      price: 'from £350'
    }, {
      name: {
        en: 'Frenectomy',
        ua: 'Френектомія',
        ru: 'Френэктомия',
        ro: 'Frenectomie'
      },
      price: '£250–350'
    }]
  }, {
    id: 'dentures',
    anchorId: 'dentures',
    title: {
      en: 'Dentures',
      ua: 'Протези',
      ru: 'Протезы',
      ro: 'Proteze Dentare'
    },
    items: [{
      name: {
        en: 'Full Acrylic Denture (one arch)',
        ua: 'Повний акриловий протез (одна щелепа)',
        ru: 'Полный акриловый протез (одна челюсть)',
        ro: 'Proteză Acrilică Totală (o arcadă)'
      },
      price: '£900'
    }, {
      name: {
        en: 'Partial Acrylic Denture',
        ua: 'Частковий акриловий протез',
        ru: 'Частичный акриловый протез',
        ro: 'Proteză Acrilică Parțială'
      },
      price: '£600'
    }, {
      name: {
        en: 'Chrome Cobalt Dentures',
        ua: 'Бюгельний протез',
        ru: 'Бюгельный протез',
        ro: 'Proteze Crom-Cobalt'
      },
      price: '£1100'
    }, {
      name: {
        en: 'Denture Repair',
        ua: 'Ремонт протеза',
        ru: 'Ремонт протеза',
        ro: 'Reparare Proteză'
      },
      price: 'from £250'
    }]
  }, {
    id: 'crowns-veneers',
    anchorId: 'crowns-veneers',
    title: {
      en: 'Crowns, Veneers & Bridges',
      ua: 'Коронки, вініри та мости',
      ru: 'Коронки, виниры и мосты',
      ro: 'Coroane, Fațete și Punți'
    },
    items: [{
      name: {
        en: 'Metal-Ceramic Crown',
        ua: 'Металокерамічна коронка',
        ru: 'Металлокерамическая коронка',
        ro: 'Coroană Metalo-Ceramică'
      },
      price: '£550'
    }, {
      name: {
        en: 'Zirconia Crown',
        ua: 'Цирконієва коронка',
        ru: 'Циркониевая коронка',
        ro: 'Coroană de Zirconiu'
      },
      price: '£600–750'
    }, {
      name: {
        en: 'E.max Veneer',
        ua: 'Вінір E.max',
        ru: 'Винир E.max',
        ro: 'Fațetă E.max'
      },
      price: '£600–750'
    }, {
      name: {
        en: 'Composite Veneer',
        ua: 'Композитний вінір',
        ru: 'Композитный винир',
        ro: 'Fațetă Compozită'
      },
      price: '£350'
    }, {
      name: {
        en: 'Porcelain Bridge (per unit)',
        ua: 'Керамічний міст (1 одиниця)',
        ru: 'Керамический мост (1 единица)',
        ro: 'Punte de Porțelan (per unitate)'
      },
      price: '£600'
    }]
  }, {
    id: 'orthodontics',
    anchorId: 'orthodontics',
    title: {
      en: 'Orthodontics',
      ua: 'Ортодонтія',
      ru: 'Ортодонтия',
      ro: 'Ortodonție'
    },
    items: [{
      name: {
        en: 'Orthodontic consultation',
        ua: 'Ортодонтична консультація',
        ru: 'Ортодонтическая консультация',
        ro: 'Consultație ortodontică'
      },
      price: '£70'
    }, {
      name: {
        en: 'Clear Aligners — Mild Case',
        ua: 'Прозорі елайнери — легкий випадок',
        ru: 'Прозрачные элайнеры — лёгкий случай',
        ro: 'Aligneri transparenți — caz ușor'
      },
      price: 'from £2,995'
    }, {
      name: {
        en: 'Clear Aligners — Moderate Case',
        ua: 'Прозорі елайнери — помірний випадок',
        ru: 'Прозрачные элайнеры — умеренный случай',
        ro: 'Aligneri transparenți — caz moderat'
      },
      price: 'from £3,995'
    }, {
      name: {
        en: 'Clear Aligners — Complex Case',
        ua: 'Прозорі елайнери — складний випадок',
        ru: 'Прозрачные элайнеры — сложный случай',
        ro: 'Aligneri transparenți — caz complex'
      },
      price: 'from £4,995'
    }, {
      name: {
        en: 'Metal braces (1 arch)',
        ua: 'Металеві брекети (1 щелепа)',
        ru: 'Металлические брекеты (1 челюсть)',
        ro: 'Aparat dentar metalic (1 arcadă)'
      },
      price: '£600'
    }, {
      name: {
        en: 'Metal braces (2 arches)',
        ua: 'Металеві брекети (2 щелепи)',
        ru: 'Металлические брекеты (2 челюсти)',
        ro: 'Aparat dentar metalic (2 arcade)'
      },
      price: '£1,100'
    }, {
      name: {
        en: 'Ceramic braces (1 arch)',
        ua: 'Керамічні брекети (1 щелепа)',
        ru: 'Керамические брекеты (1 челюсть)',
        ro: 'Aparat dentar ceramic (1 arcadă)'
      },
      price: '£800'
    }, {
      name: {
        en: 'Ceramic braces (2 arches)',
        ua: 'Керамічні брекети (2 щелепи)',
        ru: 'Керамические брекеты (2 челюсти)',
        ro: 'Aparat dentar ceramic (2 arcade)'
      },
      price: '£1,600'
    }, {
      name: {
        en: 'Tightening / adjustment appointment',
        ua: 'Активація / корекція брекетів',
        ru: 'Активация / коррекция брекетов',
        ro: 'Activare / ajustare aparat dentar'
      },
      price: '£160'
    }, {
      name: {
        en: 'Removable retainer',
        ua: 'Знімний ретейнер',
        ru: 'Съёмный ретейнер',
        ro: 'Retainer mobil'
      },
      price: '£200'
    }, {
      name: {
        en: 'Fixed metal retainer',
        ua: 'Незнімний металевий ретейнер',
        ru: 'Несъёмный металлический ретейнер',
        ro: 'Retainer metalic fix'
      },
      price: '£200'
    }, {
      name: {
        en: 'Sports mouthguard',
        ua: 'Спортивна капа',
        ru: 'Спортивная капа',
        ro: 'Gutieră sportivă'
      },
      price: 'from £250'
    }]
  }, {
    id: 'radiography',
    anchorId: 'radiography',
    title: {
      en: 'Radiography',
      ua: 'Рентгенографія',
      ru: 'Рентгенография',
      ro: 'Radiografie'
    },
    items: [{
      name: {
        en: 'Small X-ray',
        ua: 'Малий знімок',
        ru: 'Малый снимок',
        ro: 'Radiografie Mică'
      },
      price: '£20'
    }, {
      name: {
        en: 'OPG',
        ua: 'Панорамний знімок (OPG)',
        ru: 'Панорамный снимок (OPG)',
        ro: 'OPG'
      },
      price: '£50'
    }, {
      name: {
        en: 'Cephalometric',
        ua: 'Цефалометричний знімок',
        ru: 'Цефалометрический снимок',
        ro: 'Cefalometrică'
      },
      price: '£50'
    }, {
      name: {
        en: 'CBCT (single jaw)',
        ua: 'КТ (одна щелепа)',
        ru: 'КТ (одна челюсть)',
        ro: 'CBCT (o arcadă)'
      },
      price: '£100'
    }, {
      name: {
        en: 'CBCT (both jaws)',
        ua: 'КТ (обидві щелепи)',
        ru: 'КТ (обе челюсти)',
        ro: 'CBCT (ambele arcade)'
      },
      price: '£150'
    }, {
      name: {
        en: 'CBCT (segment)',
        ua: 'КТ (сегмент)',
        ru: 'КТ (сегмент)',
        ro: 'CBCT (segment)'
      },
      price: '£80'
    }]
  }, {
    id: 'indirect',
    anchorId: 'indirect',
    title: {
      en: 'Indirect Restorations',
      ua: 'Непрямі реставрації',
      ru: 'Непрямые реставрации',
      ro: 'Restaurări Indirecte'
    },
    items: [{
      name: {
        en: 'Ceramic Inlay',
        ua: 'Керамічна вкладка',
        ru: 'Керамическая вкладка',
        ro: 'Inlay Ceramic'
      },
      price: '£400'
    }, {
      name: {
        en: 'Ceramic Onlay',
        ua: 'Керамічна накладка',
        ru: 'Керамическая накладка',
        ro: 'Onlay Ceramic'
      },
      price: '£450'
    }, {
      name: {
        en: 'Ceramic Overlay',
        ua: 'Керамічний оверлей',
        ru: 'Керамический оверлей',
        ro: 'Overlay Ceramic'
      },
      price: '£500'
    }]
  }, {
    id: 'implants',
    anchorId: 'implants',
    title: {
      en: 'Dental Implants',
      ua: 'Імплантація',
      ru: 'Имплантация',
      ro: 'Implanturi Dentare'
    },
    items: [{
      name: {
        en: 'Dental implant with crown',
        ua: 'Імплант із коронкою',
        ru: 'Имплант с коронкой',
        ro: 'Implant dentar cu coroană'
      },
      price: '£2,200'
    }, {
      name: {
        en: 'Bone grafting / augmentation',
        ua: 'Підсадка / нарощування кістки',
        ru: 'Костная пластика / наращивание кости',
        ro: 'Adiție osoasă / augmentare osoasă'
      },
      price: '£650'
    }]
  }, {
    id: 'aesthetic',
    anchorId: 'injectables',
    title: {
      en: 'Injectables & Aesthetic',
      ua: "Ін'єкційна косметологія",
      ru: 'Инъекционная косметология',
      ro: 'Injectabile și Estetică'
    },
    items: [{
      name: {
        en: 'Botox',
        ua: 'Ботокс',
        ru: 'Ботокс',
        ro: 'Botox'
      },
      price: 'from £250'
    }, {
      name: {
        en: 'Hydrafacial',
        ua: 'Гідрафейшл',
        ru: 'Гидрафейшл',
        ro: 'Hydrafacial'
      },
      price: '£250'
    }]
  }];
  // Robust hash-based scroll — retries until element is found (mobile timing fix)
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (!hash) {
      hasScrolledRef.current = false;
      return;
    }
    const matchedCategory = categories.find(cat => cat.anchorId === hash);
    if (!matchedCategory) return;
    // Open the accordion and highlight
    setOpenId(matchedCategory.id);
    setHighlightedAnchor(matchedCategory.anchorId);
    // Scroll to element with retry logic for mobile
    const HEADER_OFFSET = 100; // sticky header height + padding
    let attempts = 0;
    const maxAttempts = 20; // retry for ~2 seconds
    const tryScroll = () => {
      const el = document.getElementById(hash);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
        window.scrollTo({
          top,
          behavior: 'smooth'
        });
        hasScrolledRef.current = true;
        return true;
      }
      return false;
    };
    // Try immediately
    if (tryScroll()) {
      // Also re-scroll after a beat for layout shifts
      setTimeout(tryScroll, 300);
    } else {
      // Retry with interval
      const interval = setInterval(() => {
        attempts++;
        if (tryScroll() || attempts >= maxAttempts) {
          clearInterval(interval);
          // One more attempt after accordion animation
          if (attempts < maxAttempts) {
            setTimeout(tryScroll, 350);
          }
        }
      }, 100);
      return () => clearInterval(interval);
    }
    // Remove highlight after 2.5s
    const highlightTimer = setTimeout(() => {
      setHighlightedAnchor(null);
    }, 2500);
    return () => clearTimeout(highlightTimer);
  }, [location.hash, location.key]);
  const midpoint = Math.ceil(categories.length / 2);
  const leftColumn = categories.slice(0, midpoint);
  const rightColumn = categories.slice(midpoint);
  const aligner = {
    title: {
      en: 'Clear Aligner Treatment',
      ua: 'Лікування прозорими елайнерами',
      ru: 'Лечение прозрачными элайнерами',
      ro: 'Tratament cu Aligneri Transparenți'
    },
    subtitle: {
      en: 'Discreet teeth straightening with clear removable aligners, planned individually by our orthodontic team.',
      ua: 'Непомітне вирівнювання зубів за допомогою прозорих знімних елайнерів, що плануються індивідуально нашою командою ортодонтів.',
      ru: 'Незаметное выравнивание зубов с помощью прозрачных съёмных элайнеров, разрабатываемых индивидуально нашей командой ортодонтов.',
      ro: 'Îndreptarea discretă a dinților cu aligneri transparenți detașabili, planificați individual de echipa noastră de ortodonți.'
    },
    mostPopular: {
      en: 'Most Common',
      ua: 'Найпопулярніше',
      ru: 'Самый популярный',
      ro: 'Cel mai ales'
    },
    plans: [{
      name: {
        en: 'Mild Case',
        ua: 'Легкий випадок',
        ru: 'Лёгкий случай',
        ro: 'Caz Ușor'
      },
      price: 'from £2,995',
      desc: {
        en: 'Suitable for minor tooth movement and simple alignment cases.',
        ua: 'Підходить для незначного переміщення зубів та простого вирівнювання.',
        ru: 'Подходит для небольшого перемещения зубов и простого выравнивания.',
        ro: 'Potrivit pentru mișcări dentare minore și cazuri simple de aliniere.'
      }
    }, {
      name: {
        en: 'Moderate Case',
        ua: 'Помірний випадок',
        ru: 'Умеренный случай',
        ro: 'Caz Moderat'
      },
      price: 'from £3,995',
      desc: {
        en: 'Suitable for more comprehensive upper and lower arch alignment.',
        ua: 'Підходить для більш комплексного вирівнювання верхньої та нижньої щелеп.',
        ru: 'Подходит для более комплексного выравнивания верхней и нижней челюстей.',
        ro: 'Potrivit pentru o aliniere mai amplă a arcadelor superioară și inferioară.'
      }
    }, {
      name: {
        en: 'Complex Case',
        ua: 'Складний випадок',
        ru: 'Сложный случай',
        ro: 'Caz Complex'
      },
      price: 'from £4,995',
      desc: {
        en: 'Suitable for more advanced cases requiring a more detailed treatment plan.',
        ua: 'Підходить для складніших випадків, що потребують детальнішого плану лікування.',
        ru: 'Подходит для более сложных случаев, требующих детального плана лечения.',
        ro: 'Potrivit pentru cazuri mai avansate care necesită un plan de tratament detaliat.'
      }
    }],
    note: {
      en: 'The final treatment cost depends on the complexity of your case and will be confirmed after your consultation, 3D scan and orthodontic assessment.',
      ua: 'Остаточна вартість лікування залежить від складності вашого випадку та буде підтверджена після консультації, 3D-сканування та ортодонтичного огляду.',
      ru: 'Итоговая стоимость лечения зависит от сложности вашего случая и будет подтверждена после консультации, 3D-сканирования и ортодонтической оценки.',
      ro: 'Costul final al tratamentului depinde de complexitatea cazului dvs. și va fi confirmat după consultație, scanarea 3D și evaluarea ortodontică.'
    },
    cta: {
      en: 'Book Clear Aligner Consultation',
      ua: 'Записатися на консультацію щодо елайнерів',
      ru: 'Записаться на консультацию по элайнерам',
      ro: 'Programare Consultație Aligneri'
    },
    smallText: {
      en: 'Payment options may be available. Retainers may be required after treatment and will be discussed during your consultation.',
      ua: 'Можливі варіанти оплати частинами. Після лікування можуть знадобитися ретейнери, що обговорюється під час консультації.',
      ru: 'Возможны варианты оплаты в рассрочку. После лечения могут потребоваться ретейнеры, что обсуждается на консультации.',
      ro: 'Pot fi disponibile opțiuni de plată. După tratament pot fi necesari retaineri, aspect discutat în cadrul consultației.'
    }
  };
  const alignerLang = lang as 'en' | 'ua' | 'ru' | 'ro';
  return <Layout title={seoTitle} description={seoDescription} data-id="element-2868">
      {/* Hero */}
      <section className="bg-forest-900 py-16 lg:py-20 text-white" data-id="element-2869">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2870">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-2871">
            {lang === 'en' ? 'Dental Prices in Angel, London' : lang === 'ua' ? 'Ціни на стоматологію в Angel, Лондон' : lang === 'ro' ? 'Prețuri Dentare în Angel, Londra' : 'Цены на стоматологию в Angel, Лондон'}
          </h1>
          <p className="text-lg text-ivory/80 max-w-2xl mx-auto" data-id="element-2872">
            {lang === 'en' ? 'We believe patients should feel informed before starting treatment. Our private dental fees are shown clearly below, and your dentist will confirm the final cost after consultation and assessment.' : lang === 'ua' ? 'Ми віримо, що пацієнти повинні бути поінформовані перед початком лікування. Наші ціни на приватну стоматологію чітко вказані нижче, а ваш стоматолог підтвердить остаточну вартість після консультації та огляду.' : lang === 'ro' ? 'Credem că pacienții ar trebui să se simtă informați înainte de a începe tratamentul. Tarifele noastre dentare private sunt afișate clar mai jos, iar medicul dentist va confirma costul final după consultație și evaluare.' : 'Мы верим, что пациенты должны быть проинформированы перед началом лечения. Наши цены на частную стоматологию четко указаны ниже, а ваш стоматолог подтвердит окончательную стоимость после консультации и осмотра.'}
          </p>
        </div>
      </section>

      {/* Accordion Price List */}
      <section className="py-12 lg:py-20 bg-ivory-200" data-id="element-2873">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2874">
          <div className="grid lg:grid-cols-2 gap-x-10 gap-y-0" data-id="element-2875">
            <div className="bg-ivory rounded-2xl shadow-sm border border-beige/60 px-6 sm:px-8 py-2 mb-4 lg:mb-0" data-id="element-2876">
              {leftColumn.map(cat => <AccordionItem key={cat.id} category={cat} isOpen={openId === cat.id} onToggle={() => toggle(cat.id)} lang={lang as 'en' | 'ua' | 'ru' | 'ro'} isHighlighted={highlightedAnchor === cat.anchorId} data-id="element-2877" />)}
            </div>
            <div className="bg-ivory rounded-2xl shadow-sm border border-beige/60 px-6 sm:px-8 py-2" data-id="element-2878">
              {rightColumn.map(cat => <AccordionItem key={cat.id} category={cat} isOpen={openId === cat.id} onToggle={() => toggle(cat.id)} lang={lang as 'en' | 'ua' | 'ru' | 'ro'} isHighlighted={highlightedAnchor === cat.anchorId} data-id="element-2879" />)}
            </div>
          </div>

          {/* Note */}
          <p className="mt-10 text-center text-stone-500 text-sm max-w-2xl mx-auto" data-id="element-2880">
            {lang === 'en' ? 'Prices may vary depending on clinical complexity. A consultation is required before confirming a final treatment plan.' : lang === 'ua' ? 'Ціни можуть змінюватися залежно від клінічної складності. Перед підтвердженням остаточного плану лікування необхідна консультація.' : lang === 'ro' ? 'Prețurile pot varia în funcție de complexitatea clinică. Este necesară o consultație înainte de a confirma un plan de tratament final.' : 'Цены могут варьироваться в зависимости от клинической сложности. Перед подтверждением окончательного плана лечения необходима консультация.'}
          </p>

          {/* Footer note */}
          <p className="mt-2 text-center text-stone-500 text-xs max-w-2xl mx-auto" data-id="element-2881">
            {lang === 'en' ? 'Prices may vary. Consultation required.' : lang === 'ua' ? 'Ціни можуть відрізнятися. Необхідна консультація.' : lang === 'ro' ? 'Prețurile pot varia. Este necesară o consultație.' : 'Цены могут отличаться. Необходима консультация.'}
          </p>

          {/* CTA Buttons */}
          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-3" data-id="element-2882">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-2883">
              <Button size="lg" className="gap-2 w-full sm:w-auto" data-id="element-2884">
                <Calendar className="w-5 h-5" data-id="element-2885" />
                {t('prices.bookCta')}
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-id="element-2886">
              <Button variant="whatsapp" size="lg" className="gap-2 w-full sm:w-auto" data-id="element-2887">
                <MessageCircle className="w-5 h-5" data-id="element-2888" />
                WhatsApp
              </Button>
            </a>
            <Link to="/price-list" target="_blank" rel="noopener noreferrer" data-id="element-2889">
              <Button variant="secondary" size="lg" className="gap-2 w-full sm:w-auto" data-id="element-2890">
                <Download className="w-5 h-5" data-id="element-2891" />
                {lang === 'en' ? 'Print Price List' : lang === 'ua' ? 'Роздрукувати прайс' : lang === 'ro' ? 'Imprimare Prețuri' : 'Распечатать прайс'}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Clear Aligner Treatment */}
      <section className="py-16 lg:py-24 bg-ivory" data-id="element-2892">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2893">
          <div className="text-center max-w-2xl mx-auto mb-12" data-id="element-2894">
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4" data-id="element-2895">
              {aligner.title[alignerLang]}
            </h2>
            <p className="text-base md:text-lg text-charcoal-light leading-relaxed" data-id="element-2896">
              {aligner.subtitle[alignerLang]}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-7 items-stretch" data-id="element-2897">
            {aligner.plans.map((plan, idx) => {
            const isFeatured = idx === 1;
            return <div key={idx} className={`relative flex flex-col rounded-2xl bg-ivory p-8 transition-all duration-300 ${isFeatured ? 'border-2 border-forest-600 shadow-xl md:-translate-y-2' : 'border border-beige/60 shadow-sm hover:shadow-md'}`} data-id="element-2898">
                  {isFeatured && <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-forest-600 px-4 py-1 text-xs font-semibold uppercase tracking-wide text-white shadow-sm" data-id="element-2899">
                      {aligner.mostPopular[alignerLang]}
                    </span>}
                  <h3 className={`text-xl font-bold ${isFeatured ? 'text-forest-700' : 'text-charcoal'}`} data-id="element-2900">
                    {plan.name[alignerLang]}
                  </h3>
                  <p className="mt-4 mb-1 text-4xl font-bold text-charcoal tabular-nums" data-id="element-2901">
                    {plan.price}
                  </p>
                  <div className={`my-5 h-px w-full ${isFeatured ? 'bg-forest-100' : 'bg-beige/60'}`} data-id="element-2902" />
                  <p className="text-sm leading-relaxed text-charcoal-light flex-grow" data-id="element-2903">
                    {plan.desc[alignerLang]}
                  </p>
                </div>;
          })}
          </div>

          {/* Note */}
          <p className="mt-10 text-center text-sm text-stone-500 max-w-3xl mx-auto leading-relaxed" data-id="element-2904">
            {aligner.note[alignerLang]}
          </p>

          {/* CTA */}
          <div className="mt-8 flex justify-center" data-id="element-2905">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-2906">
              <Button size="lg" className="gap-2" data-id="element-2907">
                <Calendar className="w-5 h-5" data-id="element-2908" />
                {aligner.cta[alignerLang]}
              </Button>
            </a>
          </div>

          {/* Optional small text */}
          <p className="mt-6 text-center text-xs text-stone-500 max-w-2xl mx-auto leading-relaxed" data-id="element-2909">
            {aligner.smallText[alignerLang]}
          </p>
        </div>
      </section>

      {/* Services Link */}
      <section className="py-16 bg-ivory-200" data-id="element-2910">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-2911">
          <h2 className="text-2xl font-bold text-charcoal mb-4" data-id="element-2912">
            {t('services.title')}
          </h2>
          <p className="text-charcoal-light mb-8" data-id="element-2913">{t('services.subtitle')}</p>
          <Link to={`/${lang}/services`} data-id="element-2914">
            <Button variant="secondary" size="lg" data-id="element-2915">
              {t('services.viewAll')}
            </Button>
          </Link>
        </div>
      </section>
    </Layout>;
}